import React, { useEffect } from 'react';

const Toast = ({
  message,
  type = 'info', // 'success', 'error', 'warning', 'info'
  isVisible,
  onClose,
  duration = 4000,
  position = 'top-right' // 'top-right', 'top-left', 'bottom-right', 'bottom-left'
}) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const typeConfig = {
    success: {
      icon: '✅',
      class: 'toast-success'
    },
    error: {
      icon: '❌',
      class: 'toast-error'
    },
    warning: {
      icon: '⚠️',
      class: 'toast-warning'
    },
    info: {
      icon: 'ℹ️',
      class: 'toast-info'
    }
  };

  const positionClasses = {
    'top-right': 'top-4 end-4',
    'top-left': 'top-4 start-4',
    'bottom-right': 'bottom-4 end-4',
    'bottom-left': 'bottom-4 start-4'
  };

  const config = typeConfig[type] || typeConfig.info;

  return (
    <div className={`toast ${config.class} ${positionClasses[position]}`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0 animate-scaleIn">{config.icon}</span>

        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-relaxed break-words">
            {message}
          </p>
        </div>

        <button
          onClick={onClose}
          className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center transition-all duration-200 hover:rotate-90"
        >
          <span className="text-lg leading-none">×</span>
        </button>
      </div>

      {/* Progress bar */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 h-1 bg-white/30 rounded-full overflow-hidden w-full">
          <div
            className="h-full bg-white/50 rounded-full animate-shrink-width"
            style={{
              animation: `shrinkWidth ${duration}ms linear`
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

// Toast hook for easy usage
export const useToast = () => {
  const [toast, setToast] = React.useState({
    isVisible: false,
    message: '',
    type: 'info'
  });

  const showToast = (message, type = 'info', duration = 4000) => {
    setToast({
      isVisible: true,
      message,
      type,
      duration
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const ToastComponent = () => (
    <Toast
      {...toast}
      onClose={hideToast}
    />
  );

  return {
    showToast,
    hideToast,
    ToastComponent
  };
};

export default Toast;
